// Libs
import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { check } from '@ibinder/common-icons';

// Components
import {
  Button,
  RecurrenceVisualisation,
  Chip,
  checkDocumentTypeAndRenderIcon,
  Flexbox
} from '@ibinder/common-frontend';

// Utils
import { recurrenceOutput } from './inspectionTypeTransformations';

// Styles
const Wrapper = styled.div`
  height: calc(90vh - 6rem); /* Height of modal, minus header and footer */
  display: flex;
  flex-direction: column;
`;

const InfoContainer = styled.section`
  overflow-y: scroll;
  overflow-x: hidden;
  padding: 0 1rem;

  > * {
    margin-bottom: 1rem;
    margin-left: 0;
  }
`;

const Label = styled.h4`
  color: ${(p) => p.theme.colors.discrete};
  font-size: ${(p) => p.theme.fontStyles.smallBoldFont.fontSize}px;
  font-weight: ${(p) => p.theme.fontStyles.smallBoldFont.fontWeight};
  margin-top: 2rem;
  margin-bottom: 1rem;

  &:first-child {
    margin-top: 0;
  }
`;

const SubLabel = styled.h5`
  font-weight: ${(p) => p.theme.fontStyles.smallFont.fontWeight};
  margin-bottom: 0rem;
`;

const ItemDesc = styled.p`
  margin-bottom: 1rem;
`;

const RecurDesc = styled.div`
  display: flex;

  > * {
    margin-right: 0.25rem;
  }
`;

const DocumentChip = styled(Chip)`
  svg {
    width: 36px;
  }
`;

const Footer = styled.footer`
  display: flex;
  justify-content: space-between;
  padding-top: 1rem;
  border-top: 1px solid;
  border-color: ${(p) => p.theme.colors.discrete30};
  margin-top: 1rem;

  > * {
    width: auto;
  }
`;

const StyledFlexbox = styled(Flexbox)`
  gap: 0.5rem;
`;

// Labels
const recurrenceWindowLabels = {
  earliest: 'Earliest date',
  latest: 'Latest date',
  dateheader: 'Inspection date',
  before: 'before',
  after: 'after',
  legend: 'Set inspection window'
};

const InspectionTypeView = ({
  onClose,
  inspectionType,
  onStartEdit,
  onStartDuplicate
}) => {
  const { t } = useTranslation();

  return (
    <Wrapper>
      <InfoContainer>
        <Label>{t('Type')}</Label>

        <SubLabel>{t('Name')}</SubLabel>
        <ItemDesc>{inspectionType.name}</ItemDesc>

        <SubLabel>{t('Description')}</SubLabel>
        <ItemDesc>{inspectionType.description}</ItemDesc>

        <SubLabel>{t('Inspection category')}</SubLabel>
        <ItemDesc>{inspectionType.category}</ItemDesc>

        <SubLabel>{t('Market')}</SubLabel>
        <ItemDesc>{inspectionType.market}</ItemDesc>

        <Label>{t('Documents')}</Label>
        <SubLabel>{t('Leniency requirements')}</SubLabel>
        {inspectionType.allDocuments.leniencyDocuments.map((doc) => (
          <DocumentChip
            key={`lenfilechip-${doc.id}`}
            label={doc.name}
            onClick={(e) => e.preventDefault()}
            icon={checkDocumentTypeAndRenderIcon(doc.name)}
          />
        ))}

        <SubLabel>{t('Additional documents')}</SubLabel>
        {inspectionType.allDocuments.additionalDocuments.map((doc) => (
          <DocumentChip
            key={`doc-chip-${doc.id}`}
            label={doc.name}
            onClick={(e) => e.preventDefault()}
            icon={checkDocumentTypeAndRenderIcon(doc.name)}
          />
        ))}
        {inspectionType.showRecurrenceOptions && (
          <>
            <Label>{t('Recurrences')}</Label>
            <SubLabel>{t('Recurs every')}</SubLabel>
            <RecurDesc>
              <span>{t('Inspection interval')}</span>
              {inspectionType.inspectionInterval
                ? recurrenceOutput(t)(inspectionType.inspectionInterval)
                : t('Not set')}
            </RecurDesc>
          </>
        )}
        {inspectionType.showReminderOptions && (
          <>
            <Label>{t('Reminders')}</Label>
            <SubLabel>{t('Reminder before due date')}</SubLabel>
            <RecurDesc>
              {inspectionType.remindBeforeDueDate
                ? recurrenceOutput(t)(inspectionType.remindBeforeDueDate)
                : t('Not set')}
            </RecurDesc>
          </>
        )}
        {inspectionType.appCompatible &&
          inspectionType.propertyStructureCompatible && (
            <>
              <Label>{t('Functionality')}</Label>
              <SubLabel>{t('Supports app and property structure')}</SubLabel>
              {check}
            </>
          )}
      </InfoContainer>
      <Footer>
        <Button
          btnType="regular"
          btnStyle="secondary"
          label={t('Cancel')}
          onClick={onClose}
        />
        <StyledFlexbox>
          <Button
            btnType="regular"
            btnStyle="primary"
            label={t('Duplicate')}
            onClick={() => onStartDuplicate(inspectionType.id)}
          />
          <Button
            btnType="regular"
            btnStyle="primary"
            label={t('Edit')}
            onClick={() => onStartEdit(inspectionType.id)}
          />
        </StyledFlexbox>
      </Footer>
    </Wrapper>
  );
};

export default InspectionTypeView;
